import DatePickerWrapper from '../../../../components/DatePicker/DatePickerWrapper';
import { Avatar, Box, Chip, LinearProgress, MenuItem, Select, Skeleton, Typography } from '@mui/material';
import PhoneInTalkOutlined from '@mui/icons-material/PhoneInTalkOutlined';
import SendOutlined from '@mui/icons-material/SendOutlined';
import HandshakeOutlined from '@mui/icons-material/HandshakeOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';
import CloudDoneOutlined from '@mui/icons-material/CloudDoneOutlined';
import ErrorOutlined from '@mui/icons-material/ErrorOutlined';
import moment from "moment";
import "moment/locale/fr";
import React, { useRef, useState } from "react";
import { edit, getDetails, getPhoning } from "../../../../services/ContactService";
import { getConsultantsAndManagers } from "../../../../services/UserService";
import { getCandidatApproche, updateCandidatApproche } from "../../../../services/CandidatService";
import { useParams } from "react-router-dom";
import { withSnackbar } from "../../../../utils/SnackbarHOC";

const BLUE = '#03407b';
const GREEN = '#16a34a';

const emptyToNull = (v) => (v === "" || v === undefined ? null : v);
const isSet = (v) => v !== null && v !== undefined && v !== "";
const initials = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
const today = () => moment().startOf('day').toDate();

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '10px', background: '#fff' } };

const FieldLabel = ({ children, onToday }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            {children}
        </Typography>
        {onToday && (
            <Chip
                label="Aujourd'hui"
                size="small"
                onClick={onToday}
                sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, color: BLUE, background: '#ebf4fb', border: '1px solid #aed6f1', '&:hover': { background: '#dbeefa' } }}
            />
        )}
    </Box>
);

// One step of the prospection funnel: icon + title, its fields, and a "done" state.
const StepCard = ({ index, icon: Icon, title, hint, done, doneLabel, children }) => (
    <Box sx={{
        flex: '1 1 240px', p: 2, borderRadius: '12px', background: '#fff',
        border: `1.5px solid ${done ? '#86efac' : '#e2e8f0'}`,
        boxShadow: done ? '0 0 0 3px #f0fdf4' : '0 1px 2px rgba(15,23,42,0.04)',
        transition: 'all 0.2s ease',
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
            <Box sx={{
                width: 36, height: 36, borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: done ? '#f0fdf4' : '#ebf4fb', color: done ? GREEN : BLUE,
            }}>
                {done ? <CheckCircle sx={{ fontSize: 20 }} /> : <Icon sx={{ fontSize: 20 }} />}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em' }}>ÉTAPE {index}</Typography>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>{title}</Typography>
            </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>{children}</Box>
        <Typography sx={{ fontSize: '0.76rem', mt: 1.25, color: done ? GREEN : '#94a3b8', fontWeight: done ? 600 : 400 }}>
            {done ? doneLabel : hint}
        </Typography>
    </Box>
);

function Prospection({ snackbarShowMessage, idCurrentUser }) {
    const { id, idContact, idCandidat } = useParams();
    const [loadingContact, setLoadingContact] = useState(true);
    const [loadingLists, setLoadingLists] = useState(true);
    const [phoningList, setPhoningList] = useState([]);
    const [managers, setManagers] = useState([]);
    const [saveState, setSaveState] = useState({ status: 'idle', at: null });
    // The API expects the full contact on save, so the loaded contact is kept as-is.
    const [contact, setContact] = useState(null);
    const [values, setValues] = useState({ phone: "", datePhoning: null, dmrLKD: null, dmr: null, accountManager: idCurrentUser });
    const saveQueue = useRef(Promise.resolve());

    React.useEffect(() => {
        let mounted = true;
        Promise.all([getPhoning(), getConsultantsAndManagers()])
            .then(([phoningData, managerData]) => {
                if (!mounted) return;
                setPhoningList(phoningData.data || []);
                setManagers(managerData.data.managers || []);
            })
            .catch((error) => snackbarShowMessage("Une erreur est survenue: " + error.message, "error", 4000))
            .finally(() => mounted && setLoadingLists(false));
        return () => { mounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Same tab on a contact page and on a candidate page ("Approche"): each has its own storage.
    const isCandidat = !idContact && !!idCandidat;

    React.useEffect(() => {
        let active = true;
        const load = isCandidat ? getCandidatApproche(idCandidat) : getDetails(idContact);
        load
            .then(({ data }) => {
                if (!active) return;
                setContact(data || {});
                setValues({
                    phone: data?.phoning || "",
                    datePhoning: emptyToNull(data?.datePhoning),
                    dmrLKD: emptyToNull(data?.dmrLKD),
                    dmr: emptyToNull(data?.dmr),
                    accountManager: data?.accountManager || idCurrentUser,
                });
            })
            .catch(() => active && snackbarShowMessage("Erreur de chargement de la prospection.", "error", 4000))
            .finally(() => active && setLoadingContact(false));
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idContact, idCandidat]);

    // Build the payload from the new values: React state is not updated yet at this point.
    const save = (changes) => {
        const next = { ...values, ...changes };
        setValues(next);
        if (!contact) return;
        setSaveState({ status: 'saving', at: null });
        if (isCandidat) {
            const toDay = (d) => (d && moment(d).isValid() ? moment(d).format('YYYY-MM-DD') : null);
            saveQueue.current = saveQueue.current
                .then(() => updateCandidatApproche(idCandidat, {
                    phoning: next.phone || null,
                    datePhoning: toDay(next.datePhoning),
                    dmrLKD: toDay(next.dmrLKD),
                    dmr: toDay(next.dmr),
                    accountManager: next.accountManager || null,
                }))
                .then(() => setSaveState({ status: 'saved', at: new Date() }))
                .catch((error) => {
                    setSaveState({ status: 'error', at: null });
                    snackbarShowMessage("Erreur d'enregistrement : " + error.message, "error", 4000);
                });
            return;
        }
        const payload = {
            titre: contact.titre,
            nom: contact.nom,
            prenom: contact.prenom,
            fonction: contact.fonction,
            statut: contact.statut,
            accountManager: next.accountManager,
            email: contact.email,
            mobile: contact.mobile,
            telFixDirect: contact.telFix,
            linkedIn: contact.linkedIn,
            sachezQue: contact.sachezQue,
            societe: id,
            direction: contact.direction,
            premiercontact: emptyToNull(contact.premiercontact),
            derniercontact: emptyToNull(contact.derniercontact),
            source: contact.source,
            step: contact.step,
            phoning: next.phone,
            dmrLKD: next.dmrLKD,
            dmr: next.dmr,
            datePhoning: next.datePhoning,
        };
        // Queue the saves so quick successive edits reach the server in order.
        saveQueue.current = saveQueue.current
            .then(() => edit(payload, idContact))
            .then(() => setSaveState({ status: 'saved', at: new Date() }))
            .catch((error) => {
                setSaveState({ status: 'error', at: null });
                snackbarShowMessage("Erreur d'enregistrement : " + error.message, "error", 4000);
            });
    };

    const phoningLabel = phoningList.find((p) => p.uuid === values.phone)?.nom;
    const manager = managers.find((m) => m.uuid === values.accountManager);
    const steps = [isSet(values.phone) || isSet(values.datePhoning), isSet(values.dmrLKD), isSet(values.dmr)];
    const doneCount = steps.filter(Boolean).length;
    const nextStep = ['le phoning', 'la demande LinkedIn', "l'acceptation de la demande"][steps.indexOf(false)];
    const fmt = (d) => (d && moment(d).isValid() ? moment(d).locale('fr').format('D MMMM YYYY') : '');

    if (loadingContact) {
        return (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Skeleton variant="rounded" height={72} sx={{ borderRadius: '12px' }} />
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={200} sx={{ flex: 1, borderRadius: '12px' }} />)}
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 1.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ p: 2, borderRadius: '12px', background: 'linear-gradient(135deg, #f0f7fd 0%, #ffffff 100%)', border: '1px solid #d8e6f3' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1.25 }}>
                    <Box>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>Avancement de la prospection</Typography>
                        <Typography sx={{ fontSize: '0.82rem', color: '#64748b' }}>
                            {doneCount === 3
                                ? 'Toutes les étapes sont réalisées'
                                : `${doneCount} étape${doneCount > 1 ? 's' : ''} sur 3 réalisée${doneCount > 1 ? 's' : ''} — prochaine : ${nextStep}`}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.78rem', color: saveState.status === 'error' ? '#b91c1c' : '#64748b' }}>
                        {saveState.status === 'saving' && <><CloudDoneOutlined sx={{ fontSize: 16, opacity: 0.5 }} /> Enregistrement…</>}
                        {saveState.status === 'saved' && <><CloudDoneOutlined sx={{ fontSize: 16, color: GREEN }} /> Enregistré à {moment(saveState.at).format('HH:mm')}</>}
                        {saveState.status === 'error' && <><ErrorOutlined sx={{ fontSize: 16 }} /> Non enregistré</>}
                        {saveState.status === 'idle' && 'Les modifications sont enregistrées automatiquement'}
                    </Box>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={(doneCount / 3) * 100}
                    sx={{
                        height: 8, borderRadius: 4, background: '#e2e8f0',
                        '& .MuiLinearProgress-bar': { borderRadius: 4, background: doneCount === 3 ? GREEN : `linear-gradient(90deg, #0556a4, ${BLUE})` },
                    }}
                />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <StepCard index={1} icon={PhoneInTalkOutlined} title="Phoning" done={steps[0]}
                    hint="Indiquez le résultat de l'appel et sa date."
                    doneLabel={[phoningLabel, fmt(values.datePhoning) && `le ${fmt(values.datePhoning)}`].filter(Boolean).join(' · ') || 'Appel renseigné'}>
                    <Box>
                        <FieldLabel>Statut phoning</FieldLabel>
                        {loadingLists ? <Skeleton variant="rounded" height={40} sx={{ borderRadius: '10px' }} /> : (
                            <Select
                                fullWidth size="small" displayEmpty
                                value={phoningList.some((p) => p.uuid === values.phone) ? values.phone : ''}
                                onChange={(e) => save({ phone: e.target.value })}
                                sx={{ borderRadius: '10px' }}
                                renderValue={(v) => v
                                    ? phoningList.find((p) => p.uuid === v)?.nom
                                    : <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>Choisir un statut…</Typography>}
                            >
                                <MenuItem value=""><em>Aucun</em></MenuItem>
                                {phoningList.map((p) => <MenuItem key={p.uuid} value={p.uuid}>{p.nom}</MenuItem>)}
                            </Select>
                        )}
                    </Box>
                    <Box>
                        <FieldLabel onToday={() => save({ datePhoning: today() })}>Date phoning</FieldLabel>
                        <DatePickerWrapper fullWidth format="dd/MM/yyyy" value={values.datePhoning} sx={fieldSx}
                            onChange={(v) => save({ datePhoning: v })} />
                    </Box>
                </StepCard>

                <StepCard index={2} icon={SendOutlined} title="Demande LinkedIn" done={steps[1]}
                    hint="Date d'envoi de la demande de mise en relation (DMR)."
                    doneLabel={`Envoyée le ${fmt(values.dmrLKD)}`}>
                    <Box>
                        <FieldLabel onToday={() => save({ dmrLKD: today() })}>DMR envoyée le</FieldLabel>
                        <DatePickerWrapper fullWidth format="dd/MM/yyyy" value={values.dmrLKD} sx={fieldSx}
                            onChange={(v) => save({ dmrLKD: v })} />
                    </Box>
                </StepCard>

                <StepCard index={3} icon={HandshakeOutlined} title="DMR acceptée" done={steps[2]}
                    hint="Date à laquelle le contact a accepté la demande."
                    doneLabel={`Acceptée le ${fmt(values.dmr)}`}>
                    <Box>
                        <FieldLabel onToday={() => save({ dmr: today() })}>Acceptée le</FieldLabel>
                        <DatePickerWrapper fullWidth format="dd/MM/yyyy" value={values.dmr} sx={fieldSx}
                            onChange={(v) => save({ dmr: v })} />
                    </Box>
                </StepCard>
            </Box>

            <Box sx={{ p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', background: '#fff', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 220px' }}>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>Compte LinkedIn utilisé</Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>Le compte depuis lequel ce contact est prospecté.</Typography>
                </Box>
                <Box sx={{ flex: '1 1 260px', maxWidth: 360 }}>
                    {loadingLists ? <Skeleton variant="rounded" height={40} sx={{ borderRadius: '10px' }} /> : (
                        <Select
                            fullWidth size="small" displayEmpty
                            value={managers.some((m) => m.uuid === values.accountManager) ? values.accountManager : ''}
                            onChange={(e) => save({ accountManager: e.target.value })}
                            sx={{ borderRadius: '10px' }}
                            renderValue={() => manager ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#ebf4fb', color: BLUE }}>{initials(manager.user)}</Avatar>
                                    {manager.user}
                                </Box>
                            ) : <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>Choisir un compte…</Typography>}
                        >
                            {[...managers].sort((a, b) => (a.user || '').localeCompare(b.user || '', 'fr')).map((m) => (
                                <MenuItem key={m.uuid} value={m.uuid} sx={{ gap: 1 }}>
                                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#ebf4fb', color: BLUE }}>{initials(m.user)}</Avatar>
                                    {m.user}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export default withSnackbar(Prospection);
